import React from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { FiPlus, FiMove } from "react-icons/fi";

interface QuestionContent {
  id: number;
  priority: number;
  title: string;
}

interface Question {
  id: number;
  title: string;
  isActive: boolean;
  questionContentList: QuestionContent[];
  selectReplyType: { id: number; text: string };
  questionType: { id: number; text: string };
  questionAssessmentMethod: { id: number; text: string };
  validFrom: any;
  validTo: any;
  createdDate: any;
  modifiedDate: any;
  creator: string | null;
  modifier: string;
}

interface Props {
  questions: Question[];
  selectedQuestionIds: number[];
  onAddQuestion: (question: Question) => void;
}

const QuestionBank: React.FC<Props> = ({
  questions,
  selectedQuestionIds,
  onAddQuestion,
}) => {
  return (
    <Droppable droppableId="question-bank" isDropDisabled={true}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {questions.map((q, index) => {
              const alreadyAdded = selectedQuestionIds.includes(q.id);

              return (
                <Draggable
                  key={q.id}
                  draggableId={`bank-${q.id}`}
                  index={index}
                >
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-3 transition ${
                        snapshot.isDragging ? "shadow-lg border-[#EF5644]" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          {...dragProvided.dragHandleProps}
                          className="flex items-center gap-1 text-gray-400 text-xs cursor-grab"
                        >
                          <FiMove />
                          Drag
                        </div>

                        <div className="flex flex-wrap gap-1">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                            {q.questionType?.text}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                            {q.selectReplyType?.text}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EF5644]/10 text-[#EF5644]">
                            {q.questionAssessmentMethod?.text}
                          </span>
                        </div>
                      </div>

                      <h4 className="mt-3 text-[12px] font-extrabold text-gray-800 leading-5 min-h-[40px]">
                        {q.title}
                      </h4>

                      <div className="mt-2 flex flex-wrap gap-1">
                        {(q.questionContentList || []).slice(0, 4).map((c) => (
                          <span
                            key={c.id}
                            className="text-[10px] bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-lg text-gray-600"
                          >
                            {c.title}
                          </span>
                        ))}
                      </div>

                      <button
                        type="button"
                        disabled={alreadyAdded}
                        onClick={() => onAddQuestion(q)}
                        className={`mt-4 w-full py-2 rounded-xl text-[11px] font-bold border transition flex items-center justify-center gap-1 ${
                          alreadyAdded
                            ? "bg-green-50 text-green-600 border-green-200 cursor-not-allowed"
                            : "bg-[#EF5644]/10 text-[#EF5644] border-[#EF5644]/20 hover:bg-[#EF5644]/15"
                        }`}
                      >
                        <FiPlus />
                        {alreadyAdded ? "افزوده شده" : "افزودن"}
                      </button>
                    </div>
                  )}
                </Draggable>
              );
            })}
          </div>
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

export default QuestionBank;
